import { createTypedField, DotNetType, Guid } from 'tessa/platform';
import { ICardUIExtensionContext } from 'tessa/ui/cards';
import { CardRequest, CardService } from 'tessa/cards/service';
import { tryGetFromInfo, showLoadingOverlay, showNotEmpty} from 'tessa/ui';
import { Card, CardFile, CardRowState} from 'tessa/cards';
import { ValidationResult, ValidationResultBuilder, ValidationResultType } from 'tessa/platform/validation';
import { getFileNameAndExtension } from 'tessa/ui/cards/controls';
import { CertData, FileContainer, FileSignatureEventType, IFile, IFileVersion, readFileContentAsBase64 } from 'tessa/files';
import { CdEDSHelper, CustomCertData } from 'src/cdScripts/Helpers/CdEDSHelper';
import { CdRequestTypes } from './CdRequestTypes';
import { CdTaskTypes } from './CdTaskTypes';
import { CdFdProcessHelper } from './CdFdProcessHelper';
import { CdCardTypes } from './CdCardTypes';
import { CdDssCspType } from './CdDssCspType';
import { CdGeneralDocumentationClientHelper } from './Cards/CdGeneralDocumentationClientHelper';
import { PkCommonHelper } from 'src/pkScripts/Helpers/PkCommonHelper';

export class CdDSSHelper {

        /** Получение файлов для подписания. */
    public static async GetSigningFilesInfoAsync(
        context: ICardUIExtensionContext,
        userID: string,
        completedTaskTypeID: string | null = null,
        fileName: string | null = null,
        categoryID: guid | null = null) {
        // инициализируем результат валидации
        const validationResult = new ValidationResultBuilder();

        // инициализируем список файлов, которые нужно подписать
        const signingFilesInfo = new Array<SigningFileInfo>();

        // инициализируем список поддерживаемых расширений
        const officeFormats = new Array<string>(".docx", ".doc", ".xlns", ".xls", ".pdf");

        // пробегаемся по файлам карточки
        for (const file of context.fileContainer.files) {
            // если категория не указана
            if (file.category == null) {
                // смотрим другие файлы
                continue;
            }

            // если передана категория
            if (categoryID !== null &&
                file.category.id !== categoryID.toLowerCase()) {
                    // смотрим другие файлы
                    continue;
            }

            // если передано наименование файла, но оно не совпадает
            if (fileName != null && file.name.indexOf(fileName) < 0) {
                // смотрим другие файлы
                continue;
            }

            // получаем наименование файла и его расширение
            const fileNameAndExt = getFileNameAndExtension(file.name);

            // получаем расширения файла
            const fileExt = fileNameAndExt?.ext?.toLowerCase();

            // если в файле есть флаг печатной формы
            // или передано наименование файла, но оно не совпадает
            // или формат файла не офисный и не PFD
            if (tryGetFromInfo<boolean>(file.options, "FileForPrint", false) ||
                fileName != null && file.name.indexOf(fileName) < 0 ||
                officeFormats.indexOf(fileExt) < 0) {
                // смотрим другие файлы
                continue;
            }

            // получаем последнюю версию файла
            const lastFileVersion = file.lastVersion;

            // получаем файл из карточки
            const cardFile = context.card.files.find(x => x.rowId === file.id) as CardFile;

            // получаем или добавляем секцию с доп. инфой по подписям
            const pkfsaiSection = cardFile.card.sections.getOrAdd("PkFileSignaturesAdditionalInfo");

            // проверяем есть ли среди подписей текущего сотрудника удовлетворяющая
            const isSignatureAlreadyExist = lastFileVersion.signatures.some(x =>
                x.userId === userID &&
                pkfsaiSection.rows.some(y =>
                    y.tryGet("SignatureRowID") === x.id &&
                    y.tryGet("CompletedTaskTypeID") === completedTaskTypeID));

            // если такая подпись есть
            if (isSignatureAlreadyExist) {
                // смотрим другие файлы
                continue;
            }

            // получаем контент файла
            const lastFileVersionContentResult = await this.GetFileContentAsync(
                lastFileVersion,
                context.fileContainer);

            // проверяем ошибки
            if (!lastFileVersionContentResult.isSuccessful) {
                // пишем ошибку в результат валидации
                validationResult.add(
                    ValidationResultType.Error,
                    "Не удалось получить контент файла \"" + lastFileVersion.name + "\".",
                    undefined,
                    undefined,
                    undefined,
                    lastFileVersionContentResult.toString());

                // смотрим другие файлы
                continue;
            }

            // заполняем объект с данными по файлу
            const signingFileInfo = new SigningFileInfo(
                cardFile,
                lastFileVersion);

            // добавляем объект в список
            signingFilesInfo.push(signingFileInfo);
        }

        return { signingFilesInfo, validationResult };
    }

    /** Проверка карточки на необходимость подписания файлов. */
    public static async IsNeedSigningAsync(card: Card) {
        // флаг необходимости подписания
        let isNeedSigning = false;

        // наименование файла
        let fileName: string | null = null;

        // пробегаемся по заданиям
        for (const task of card.tasks) {
            // если задание не завершалось
            if (task.state !== CardRowState.Deleted) {
                // смотрим другие задания
                continue;
            }

            // если завершается задание "Рассмотрение куратором" по кнопке "Подписать документ"
            if (task.typeId === CdTaskTypes.PkPersonnelDocumentCuratorReviewingTypeID &&
                task.optionId === CdFdProcessHelper.PkSignDocument) {
                // устанавливаем флаг подписания
                isNeedSigning = true;
                break;
            }

            // если завершается задание "Подписание сотрудником" по кнопке "Подписать"
            if (task.typeId === CdTaskTypes.PkPersonnelDocumentSigningTypeID &&
                task.optionId === CdFdProcessHelper.Sign) {
                // устанавливаем флаг подписания
                isNeedSigning = true;
                break;
            }

            // если завершается задание "Соглашение об аннулировании куратором" по кнопке "Подписать документ"
            if (task.typeId === CdTaskTypes.PkPersonnelDocumentCuratorRevocationAgreementTypeID &&
                task.optionId === CdFdProcessHelper.PkSignDocument) {
                // устанавливаем флаги
                isNeedSigning = true;
                break;
            }

            // если завершается задание "Соглашение об аннулировании сотрудником" по кнопке "Подписать"
            if (task.typeId === CdTaskTypes.PkPersonnelDocumentEmployeeRevocationAgreementTypeID &&
                task.optionId === CdFdProcessHelper.Sign) {
                // устанавливаем флаги
                isNeedSigning = true;
                break;
            }

            // если завершается задание "Переход на ЭТК"
            if (task.typeId === CdTaskTypes.PkPersonnelDocumentEtkTransitionTypeID) {
                // по кнопке "Согласовать"
                if (task.optionId === CdFdProcessHelper.PkAgree) {
                    // устанавливаем флаг подписания и тип файла
                    isNeedSigning = true;
                    fileName = "Согласие на переход на ЭТК";
                    break;
                }

                // по кнопке "Не согласовать"
                if (task.optionId === CdFdProcessHelper.PkDisagree) {
                    // устанавливаем флаг подписания и тип файла
                    isNeedSigning = true;
                    fileName = "Согласие на сохранение БТК";
                    break;
                }
            }
        }

        return { isNeedSigning, fileName };
    }

    /** Получение информации для подписания. */
    public static async GetSigningInfoAsync(
        card: Card,
        userID: string,
        checkMainCertificate: boolean = false,
        withoutNep: boolean = false,
        withoutPikProjectCheck: boolean = false) {
        // получаем данные по сотруднику и файлам
        let signatureContent: string | null = null;
        let organizationExtID: number | null = null;
        let organizationName: string | null = null;
        let certInfo: UserCertInfo | null = null;
        let withSignatureContent = card.typeId === CdCardTypes.TechnicalDocumentationTypeID;

        // получаем секцию
        const pkpdiSection = card.sections.tryGet("PkPersonnelDocumentInfo");
        if (pkpdiSection != null) {
            organizationExtID = pkpdiSection.fields.tryGet("CompanyExternalID") as number | null;
            organizationName = pkpdiSection.fields.tryGet("CompanyName") as string | null;
        }

        // получаем данные по сертификату КЭП
        const dssUserCert = await this.GetUserCertInfoAsync(
            userID,
            undefined,
            undefined,
            organizationExtID,
            checkMainCertificate,
            CdDssCspType.Qualified,
            withSignatureContent
        );

        // получаем данные по сертификату НЭП
        const nepUserCert = withoutNep ? null : await this.GetUserCertInfoAsync(
            userID,
            undefined,
            undefined,
            organizationExtID,
            checkMainCertificate,
            CdDssCspType.NonQualified,
            withSignatureContent
        );

        // устанавливаем флаги
        const isExistsDssCert = dssUserCert != null;
        const isExistsNepCert = nepUserCert != null;
        const isQualifiedOrg = withoutPikProjectCheck ? true : this.IsOrganizationNeedQualifiedSignature(card);

        // Куратор подписывает "Кадровый документ"
        let isCuratorPersonnelSigning = this.IsCuratorPersonnelSigning(card);

        // объявляем данные для подписания
        let externalUserGuid: string | null = null;
        let employeeGuid1C: string | null = null;
        let dssCspType: CdDssCspType | null = null;
        let certIssuerName: string | null = null;
        let certEnhancedKey: string | null = null;
        let isNeedNepRelease: boolean = false;
        let isAutoSigningPersonnelDocuments: boolean = false;

        // если для подписания нужен КЭП (ПИК-Проект, ГП-МСК, ГП-МСФ) и у сотрудника есть сертификат DSS
        if ((isQualifiedOrg || isCuratorPersonnelSigning) && isExistsDssCert) {
            dssCspType = CdDssCspType.Qualified;
            externalUserGuid = dssUserCert!.ExternalUserGuid;
            employeeGuid1C = dssUserCert!.EmployeeGuid1C;
            isAutoSigningPersonnelDocuments = dssUserCert!.IsAutoSigningPersonnelDocuments;
            signatureContent = dssUserCert!.SignatureContent;
            certInfo = dssUserCert;
        }

        // иначе если у пользователя есть сертифкат НЭП и это не Подписание куратором Кадрового документа (а подписание сотрудником)
        else if (isExistsNepCert && !isCuratorPersonnelSigning) {
            dssCspType = CdDssCspType.NonQualified;
            externalUserGuid = nepUserCert!.ExternalUserGuid;
            employeeGuid1C = nepUserCert!.EmployeeGuid1C;
            isAutoSigningPersonnelDocuments = nepUserCert!.IsAutoSigningPersonnelDocuments;
            signatureContent = nepUserCert!.SignatureContent;
            certInfo = nepUserCert;
        }

        // если это подписание не куратором, а сотрудником
        else if (!isCuratorPersonnelSigning) {

            // проверяем, переводился ли этот сотрудник из ПИК-Проекта в ГП-МСК / ГП-МФС
            // и если нет, то устанавливаем необходимость выпуска оНЭП (т.е. это обычный сотрудник, он должен подписывать оНЭПом)
            // в противном случае мы даем возможность подписать таким сотрудникам железным сертификатом
            if (!isQualifiedOrg) {
                certEnhancedKey = "1.2.643.3.7.3.1001572";
                isNeedNepRelease = true;
            }
        }

        // возвращаем данные для подписания
        return {
            externalUserGuid,
            employeeGuid1C,
            dssCspType,
            certIssuerName,
            certEnhancedKey,
            organizationExtID,
            organizationName,
            isNeedNepRelease,
            isAutoSigningPersonnelDocuments,
            signatureContent,
            certInfo
        };
    }

    /** Получение данных об организации пользователя. */
    public static async GetUserCertInfoAsync(
        userID: string,
        boxID: string | null = null,
        departmentID: string | null = null,
        organizationExtID: number | null = null,
        checkMainCertificate: boolean = false,
        dssCspType: CdDssCspType | null = null,
        withSignatureContent: boolean = false) {
        // инициализируем запрос
        const request = new CardRequest();
        request.requestType = CdRequestTypes.GetUserCertInfo;
        request.info["UserID"] = createTypedField(userID, DotNetType.Guid);
        request.info["BoxID"] = boxID != null ? createTypedField(boxID, DotNetType.Guid) : null;
        request.info["DepartmentID"] = departmentID != null ? createTypedField(departmentID, DotNetType.Guid) : null;
        request.info["OrganizationExtID"] = organizationExtID != null ? createTypedField(organizationExtID, DotNetType.Int64) : null;
        request.info["CheckMainCertificate"] = createTypedField(checkMainCertificate, DotNetType.Boolean);
        request.info["DssCspType"] = dssCspType != null ? createTypedField(dssCspType.ID, DotNetType.Int32) : null;
        request.info["WithSignatureContent"] = createTypedField(withSignatureContent, DotNetType.Boolean);

        // отправляем запрос
        const response = await CardService.instance.request(request);

        // проверяем ответ
        if (!response.validationResult.isSuccessful ||
            !tryGetFromInfo<boolean>(response.info, "IsUserHasCertificate", false)) {
            return null;
        }

        // получаем данные из ответа
        const userCertInfo: UserCertInfo = {
            ExternalUserGuid: tryGetFromInfo<string | null>(response.info, "ExternalUserGuid"),
            EmployeeGuid1C: tryGetFromInfo<string | null>(response.info, "EmployeeGuid1C"),
            IsAutoSigningPersonnelDocuments: tryGetFromInfo<boolean>(response.info, "IsAutoSigningPersonnelDocuments"),
            SignatureContent: tryGetFromInfo<string | null>(response.info, "SignatureContent"),
            CertCompany: tryGetFromInfo<string | null>(response.info, "CertCompany"),
            CertAdr: tryGetFromInfo<string | null>(response.info, "CertAdr"),
            CertOwner: tryGetFromInfo<string | null>(response.info, "CertOwner")
        };

        // возвращаем данные
        return userCertInfo;
    }

    /** Нужно ли для подписания сотрудников этой организации использование КЭП (ПИК-Проект, ГП МСК, ГП МФС). */
    private static IsOrganizationNeedQualifiedSignature(card: Card) {
        // ПИК-Проект, ГП-МСК и ГП-МФС
        const qualifiedSignatureOrgExternalIds = [ 42240, 74385, 49340 ] as number[];

        // получаем секцию
        const pkpdiSection = card.sections.tryGet("PkPersonnelDocumentInfo");
        if (pkpdiSection == null) {
            return false;
        }

        // получаем ExtID организации
        const companyExternalID = pkpdiSection.fields.tryGet("CompanyExternalID") as number;

        // проверяем является ли организация "ПИК-Проектом", "ГП-МСК" или "ГП-МФС"
        return qualifiedSignatureOrgExternalIds.includes(companyExternalID);
    }

    /** Является ли текущее подписание Подписанием куратора */
    private static IsCuratorPersonnelSigning(card: Card) {
        return card.tasks.some(task => Guid.equals(task.typeId, CdTaskTypes.PkPersonnelDocumentCuratorReviewingTypeID)
            && Guid.equals(task.optionId, CdFdProcessHelper.PkSignDocument));
    }
    
    /** Подписание файлов с помощью DSS-сервиса. */
    public static async SignFilesByDssAsync(
        context: ICardUIExtensionContext,
        userID: string,
        externalUserGuid: string | null,
        employeeGuid1C: string | null,
        dssCspType: CdDssCspType,
        certInfo: UserCertInfo | null = null,
        fileName: string | null = null,
        categoryID: guid | null = null) {
        try {
            await showLoadingOverlay(async () => {
                // получаем тип завершаемого задания
                const completedTaskTypeID = context.card.tasks.find(x => x.optionId != null)?.typeId;

                // получаем файлы для подписания
                const signingFilesInfoResult = await this.GetSigningFilesInfoAsync(
                    context,
                    userID,
                    completedTaskTypeID,
                    fileName,
                    categoryID);

                // проверяем ошибки
                if (!signingFilesInfoResult.validationResult.isSuccessful) {
                    // пробрасываем исключение
                    throw new Error(signingFilesInfoResult.validationResult.build().toString());
                }

                // если сертификат не задан и есть файлы для подписи
                if (signingFilesInfoResult.signingFilesInfo.length > 0) {
                    // инициализируем данные для подписания файлов
                    const dssRequestFilesSigningInfo = new Array<DssRequestFileSigningInfo>();

                    // для РД проставляем штамп Выдано в производство работ перед подписанием
                    if (context.card.typeId === CdCardTypes.TechnicalDocumentationTypeID) {
                        if (!certInfo) {
                            throw new Error("Не удалось получить данные о сертификате");
                        }

                        console.info('[Подписание DSS|Информация]: ' + certInfo.CertCompany + '|' + certInfo.CertOwner + '|' + certInfo.CertAdr);

                        // пробегаемся по файлам для подписания
                        for (const signingFileInfo of signingFilesInfoResult.signingFilesInfo) {
                            let fileContent = await readFileContentAsBase64(signingFileInfo.Version.content!);
                            if (fileContent) {
                                console.info('[fileContent]: ' + fileContent.length);
                                let isInWork = false;

                                if (signingFileInfo.Version.name.toLowerCase().indexOf('.pdf') >= 0)
                                {
                                    let fileWithStamp = await CdGeneralDocumentationClientHelper.GetRdWithWorkStampAsync(
                                        context.card.id,
                                        signingFileInfo.Version.id,
                                        signingFileInfo.CardFile.rowId,
                                        signingFileInfo.CardFile.name,
                                        certInfo.CertCompany!,
                                        certInfo.CertOwner!,
                                        certInfo.CertAdr!);
                                    fileContent = await PkCommonHelper.GetBase64String(fileWithStamp.Blob);
                                    isInWork = true;
                                    console.info('Файл обработан');
                                    console.info(fileContent == null ? "не удалось поставить штамп " : "штамп успешно добавлен [fileContent]: " + fileContent!.length);
                                    if (!fileContent) {
                                        throw new Error("Не удалось наложить штамп на pdf");
                                    }
                                }

                                const dssRequestFileSigningInfo = new DssRequestFileSigningInfo(
                                    signingFileInfo.Version.id,
                                    signingFileInfo.Version.name,
                                    fileContent);

                                dssRequestFileSigningInfo.IsInWork = isInWork;
                                dssRequestFilesSigningInfo.push(dssRequestFileSigningInfo);
                            }
                        }
                    }
                    else {
                        // пробегаемся по файлам для подписания
                        for (const signingFileInfo of signingFilesInfoResult.signingFilesInfo) {
                            const dssRequestFileSigningInfo = new DssRequestFileSigningInfo(
                                signingFileInfo.Version.id,
                                signingFileInfo.Version.name,
                                await readFileContentAsBase64(signingFileInfo.Version.content!));
                            dssRequestFilesSigningInfo.push(dssRequestFileSigningInfo);
                        }
                    }

                    // инициализируем запрос
                    const dssSignRequest = new CardRequest();
                    dssSignRequest.requestType = CdRequestTypes.PkSignFilesByDss;
                    dssSignRequest.info["ExternalUserGuid"] = externalUserGuid != null ? createTypedField(externalUserGuid, DotNetType.Guid) : null;
                    dssSignRequest.info["EmployeeGuid1C"] = employeeGuid1C != null ? createTypedField(employeeGuid1C, DotNetType.Guid) : null;
                    dssSignRequest.info["DssCspType"] = createTypedField(dssCspType.ID, DotNetType.Int32);
                    dssSignRequest.info["DssRequestFilesSigningInfo"] = createTypedField(JSON.stringify(dssRequestFilesSigningInfo), DotNetType.String);

                    // отправляем запрос
                    const dssSignResponse = await CardService.instance.request(dssSignRequest);

                    console.info('dssSignRequest completed');
                    // проверяем ошибки в ответе
                    if (!dssSignResponse.validationResult.isSuccessful) {
                        // пробрасываем исключение
                        throw new Error(dssSignResponse.validationResult.build().toString());
                    }

                    // получаем данные по подписанию файлов
                    const dssResponseFilesSigningInfoJson = tryGetFromInfo<string | null>(
                        dssSignResponse.info, "DssResponseFilesSigningInfo");

                    // если данных по подписанию файлов нет
                    if (dssResponseFilesSigningInfoJson == null) {
                        // получаем статус операции
                        const dssOperationStatusName = tryGetFromInfo<string | null>(
                            dssSignResponse.info, "DssOperationStatusName");

                        // пишем ошибку в контекст
                        context.validationResult.add(
                            ValidationResultType.Error,
                            dssOperationStatusName ?? "");

                        // отменяем сохранение
                        context.cancel = true;

                        // выходим
                        return;
                    }

                    // десериализуем данные
                    const dssResponseFilesSigningInfo: Array<DssResponseFileSigningInfo> = JSON.parse(dssResponseFilesSigningInfoJson);

                    // для РД отмечаем isInWork и проставляем контент, чтобы заменился файл
                    if (context.card.typeId === CdCardTypes.TechnicalDocumentationTypeID) {
                        // пробегаемся по файлам для подписания
                        for (const signingFileInfo of dssResponseFilesSigningInfo) {
                            const dssRequestInfo = dssRequestFilesSigningInfo.find(x => x.VersionRowID === signingFileInfo.VersionRowID);
                            if (!dssRequestInfo) continue;
                            signingFileInfo.IsInWork = dssRequestInfo.IsInWork;
                            signingFileInfo.ContentInBase64 = dssRequestInfo.ContentBase64;
                        }
                    }

                    await this.SendCdSignRequest(
                        dssResponseFilesSigningInfo,
                        context.card,
                        context.fileContainer.files.find(x =>
                            x.category?.id == categoryID &&
                            x.name == fileName),
                        context.fileContainer);
                }
            }, "Подписание файлов");
        } catch (e) {
            // отменяем сохранение карточки
            context.cancel = true;

            // пишем ошибку в контекст
            context.validationResult.add(
                ValidationResultType.Error,
                "Возникли ошибки при подписании файлов.\n" +
                "Пожалуйста, обратитесь к администратору, приложив детальное сообщение об ошибке.\n" +
                "Детальное сообщение можно скопировать, нажав кнопку \"Копировать\", " +
                "либо открыв его кликом по ошибке и скопировав стандартным способом.",
                undefined,
                undefined,
                undefined,
                ValidationResult.fromError(e).toString());
        }
    }

    private static async SendCdSignRequest(
        dssResponseFilesSigningInfo: Array<DssResponseFileSigningInfo>,
        card: Card,
        file: IFile | undefined,
        fileContainer: FileContainer) {

        let withEdRoles =  Guid.equals(card.typeId, CdCardTypes.PkExecutiveDocumentationTypeID);
        let withRoles =  Guid.equals(card.typeId, CdCardTypes.PkExecutiveSchemeTypeID);
        let withApprovementRoles =  Guid.equals(card.typeId, CdCardTypes.PkRequestForMaterialsTypeID);

        for (const dssResult of dssResponseFilesSigningInfo) {
            const request = new CardRequest();
            request.requestType = CdRequestTypes.SignInfo;
            request.info["VersionRowID"] = createTypedField(dssResult.VersionRowID, DotNetType.Guid);
            request.info["Comment"] = createTypedField(null, DotNetType.String);
            request.info["SubjectName"] = createTypedField(dssResult.SubjectName, DotNetType.String);
            request.info["Company"] = createTypedField(dssResult.Company, DotNetType.String);
            request.info["Signed"] = createTypedField(new Date(), DotNetType.DateTime);
            request.info["SerialNumber"] = createTypedField(dssResult.SerialNumber, DotNetType.String);
            request.info["IssuerName"] = createTypedField(dssResult.IssuerName, DotNetType.String);
            request.info["Data"] = createTypedField(dssResult.SignBase64, DotNetType.String);
            request.info["isEnhanceSign"] = createTypedField(true, DotNetType.Boolean);
            request.info["TypeID"] = createTypedField(card.typeId, DotNetType.Guid);
            request.info["WithEdRoles"] = createTypedField(withEdRoles, DotNetType.Boolean);
            request.info["WithRoles"] = createTypedField(withRoles, DotNetType.Boolean);
            request.info["WithApprovementRoles"] = createTypedField(withApprovementRoles, DotNetType.Boolean);

            const response = await CardService.instance.request(request);
            await showNotEmpty(response.validationResult.build());
            if (!response.validationResult.isSuccessful) {
                return null;
            }

            if (file && (withEdRoles || withRoles || withApprovementRoles)) {
                let certificate: CustomCertData = {
                    company: dssResult.Company,
                    subjectName: dssResult.SubjectName,
                    serialNumber: dssResult.SerialNumber,
                    issuerName: dssResult.IssuerName,
                    validFrom: dssResult.ValidFrom,
                    validTo: dssResult.ValidTo,
                    issuerFullName: dssResult.IssuerName,
                    certificateStr: '',
                    extendedKeysUsageOids: []
                };

                /* 5770 исключено из-за дублирования инфо о подписи
                await CdEDSHelper.AddSignatureToFileAsync(
                    card,
                    fileContainer,
                    file,
                    dssResult.SignBase64,
                    true,
                    certificate);
                */

                await CdEDSHelper.AddDetachedSignatureFileAsync(
                    fileContainer,
                    file.lastVersion.name,
                    certificate.ownerName ?? certificate.subjectName, 
                    dssResult.SignBase64);    
            }
        }
    }

    /** Подписание файла. */
    public static async SignFileAsync(
        cardFile: CardFile,
        fileVersion: IFileVersion,
        certificate: CertData,
        signatureData: string,
        completedTaskTypeID: string | null = null) {
        // получаем или добавляем секцию с доп. инфой по подписям
        const pkfsaiSection = cardFile.card.sections.getOrAdd("PkFileSignaturesAdditionalInfo");

        // создаем токен подписи
        const signatureToken = fileVersion.source.getSignatureCreationToken();
        signatureToken.eventType = FileSignatureEventType.Signed;
        signatureToken.company = certificate.company;
        signatureToken.subjectName = certificate.subjectName;
        signatureToken.serialNumber = certificate.serialNumber;
        signatureToken.issuerName = certificate.issuerName;
        signatureToken.data = signatureData;

        // создаем подпись
        const signature = fileVersion.source.createSignature(signatureToken, fileVersion);

        // добавляем подпись в файл
        fileVersion.signatures.push(signature);

        // уведомляем о добавлении подписи
        await fileVersion.source.notifySignAdded(signature);

        // добавляем доп. инфо по подписи
        const pkfsRow = pkfsaiSection.rows.add();
        pkfsRow.rowId = Guid.newGuid();
        pkfsRow.set("SignatureRowID", createTypedField(signature.id, DotNetType.Guid));
        if (completedTaskTypeID != null) {
            pkfsRow.set("CompletedTaskTypeID", createTypedField(completedTaskTypeID, DotNetType.Guid));
        }
        pkfsRow.state = CardRowState.Inserted;
    }

    /** Получение контента файла. */
    public static async GetFileContentAsync(
        fileVersion: IFileVersion,
        fileContainer: FileContainer) {
        // если контент файла пустой
        if (fileVersion.content == null ||
            fileVersion.content.size === 0) {
            // получаем контент
            const fileVersionContent = await fileContainer.getFileContent(fileVersion);

            // если не удалось получить контент
            if (!fileVersionContent.validationResult.isSuccessful ||
                fileVersionContent.data == null) {
                return fileVersionContent.validationResult;
            }

            // устанавливаем контент
            await fileVersion.setContent(fileVersionContent.data);
        }

        return ValidationResult.empty;
    }

}

/** Информация по файлу для подписания. */
export class SigningFileInfo {
    public CardFile: CardFile;

    public Version: IFileVersion;

    public constructor(
        cardFile: CardFile,
        version: IFileVersion) {
        this.CardFile = cardFile;
        this.Version = version;
    }
}

export class DssRequestFileSigningInfo {
    public VersionRowID: string;
    public FileName: string;
    public ContentBase64: string | null;
    public IsInWork: boolean | null;

    public constructor(
        versionRowID: string,
        fileName: string,
        contentBase64: string | null) {
        this.VersionRowID = versionRowID;
        this.FileName = fileName;
        this.ContentBase64 = contentBase64;
    }
}

export class DssResponseFileSigningInfo {
    public VersionRowID: string;
    public SignBase64: string;
    public SubjectName: string;
    public SerialNumber: string;
    public IssuerName: string;
    public Company: string;
    public ValidFrom: string;
    public ValidTo: string;
    public IsInWork: boolean | null;
    public ContentInBase64: string | null;
}

/** Информация по сертификату пользователя. */
export class UserCertInfo {
    public ExternalUserGuid: string | null;
    public EmployeeGuid1C: string | null;
    public IsAutoSigningPersonnelDocuments: boolean;
    public SignatureContent: string | null;
    public CertCompany: string | null;
    public CertOwner: string | null;
    public CertAdr: string | null;
}